'use client';
import React, { useRef, useState, useEffect } from 'react';
import { Search, MapPin, X, ChevronDown, Check } from 'lucide-react';
import type { FilterStatus } from './MapClientWrapper';
import type { Pantry } from '@/lib/pantryData';

interface MapFiltersProps {
  filterStatus: FilterStatus;
  setFilterStatus: (s: FilterStatus) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  totalVisible: number;
  onSuggestionSelect?: (pantryId: string) => void;
  pantries: Pantry[];
}

const FILTER_OPTIONS: { value: FilterStatus; label: string; dot: string }[] = [
  { value: 'lahat', label: 'Lahat', dot: 'bg-[#2d7a4f]' },
  { value: 'aktibo', label: 'Aktibo', dot: 'bg-[#16a34a]' },
  { value: 'mababa-stock', label: 'Mababa ang Stock', dot: 'bg-[#ca8a04]' },
  { value: 'walang-stock', label: 'Walang Stock', dot: 'bg-[#dc2626]' },
  { value: 'hindi-aktibo', label: 'Sarado', dot: 'bg-[#9ca3af]' },
];

const STATUS_DOT: Record<string, string> = {
  'aktibo': 'bg-[#16a34a]',
  'mababa-stock': 'bg-[#ca8a04]',
  'walang-stock': 'bg-[#dc2626]',
  'hindi-aktibo': 'bg-[#9ca3af]',
};

export default function MapFilters({
  filterStatus,
  setFilterStatus,
  searchQuery,
  setSearchQuery,
  totalVisible,
  onSuggestionSelect,
  pantries,
}: MapFiltersProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const suggestions = searchQuery.trim().length >= 1
    ? pantries.filter((p) => {
        const q = searchQuery.toLowerCase();
        return (
          p.pangalan.toLowerCase().includes(q) ||
          p.lungsod.toLowerCase().includes(q) ||
          p.barangay.toLowerCase().includes(q)
        );
      }).slice(0, 6)
    : [];

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (pantry: Pantry) => {
    setSearchQuery(pantry.pangalan);
    setShowSuggestions(false);
    onSuggestionSelect?.(pantry.id);
  };

  const handleClear = () => {
    setSearchQuery('');
    setShowSuggestions(false);
  };

  const selectedOption = FILTER_OPTIONS.find((o) => o.value === filterStatus) ?? FILTER_OPTIONS[0];

  return (
    <div className="bg-white border-b border-[#d1ead9] px-4 py-3 z-10 shadow-sm">
      <div className="max-w-screen-2xl mx-auto flex flex-row gap-3 items-center">
        {/* Search */}
        <div ref={wrapperRef} className="relative flex-1 min-w-0">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9ca3af] pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            placeholder="Maghanap ng pantry, barangay, lungsod…"
            className="w-full pl-9 pr-8 py-2 text-sm border border-[#d1ead9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] bg-[#f8fdf9] placeholder-[#9ca3af]"
          />
          {searchQuery && (
            <button
              onClick={handleClear}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9ca3af] hover:text-[#6b7280] transition-colors"
            >
              <X size={14} />
            </button>
          )}

          {/* Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-[#d1ead9] rounded-xl shadow-lg overflow-hidden">
              {suggestions.map((pantry) => (
                <button
                  key={`sug-${pantry.id}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(pantry)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#f0faf4] transition-colors text-left"
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT[pantry.status] ?? 'bg-[#9ca3af]'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1e5c38] truncate">{pantry.pangalan}</p>
                    <p className="text-xs text-[#9ca3af] truncate">{pantry.barangay}, {pantry.lungsod}</p>
                  </div>
                  <MapPin size={12} className="text-[#9ca3af] flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {/* No results */}
          {showSuggestions && searchQuery.trim().length >= 1 && suggestions.length === 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-[#d1ead9] rounded-xl shadow-lg px-4 py-3">
              <p className="text-sm text-[#9ca3af]">Walang nahanap na pantry.</p>
            </div>
          )}
        </div>

        {/* Filter Dropdown */}
        <div ref={dropdownRef} className="relative flex-shrink-0">
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-[#d1ead9] rounded-lg bg-white hover:bg-[#f8fdf9] transition-colors min-w-[130px] justify-between"
          >
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${selectedOption.dot}`} />
              <span className="text-[#374151]">{selectedOption.label}</span>
            </div>
            <ChevronDown size={14} className={`text-[#9ca3af] transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {dropdownOpen && (
            <div className="absolute top-full right-0 mt-1 z-50 bg-white border border-[#d1ead9] rounded-xl shadow-lg overflow-hidden min-w-[160px]">
              {FILTER_OPTIONS.map((opt) => (
                <button
                  key={`filter-${opt.value}`}
                  onClick={() => {
                    setFilterStatus(opt.value);
                    setDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#f0faf4] transition-colors text-left"
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${opt.dot}`} />
                  <span className="flex-1 text-sm text-[#374151]">{opt.label}</span>
                  {filterStatus === opt.value && (
                    <Check size={14} className="text-[#2d7a4f]" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Count */}
        <div className="hidden sm:flex items-center gap-1.5 text-xs text-[#6b7280] flex-shrink-0">
          <MapPin size={13} />
          <span><strong className="text-[#2d7a4f] font-semibold">{mounted ? totalVisible : 0}</strong> pantry</span>
        </div>
      </div>
    </div>
  );
}